/**
 * ImportReportView - Component for displaying CSV import results
 *
 * Shows success/failure summary with detailed error messages
 */
package com.castly.admin.ui.components

import androidx.compose.runtime.*
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

data class ImportSummary(
    val totalRows: Int,
    val successCount: Int,
    val failureCount: Int,
    val skippedCount: Int = 0,
    val errors: List<ImportError> = emptyList(),
    val durationMs: Long? = null
)

data class ImportError(
    val row: Int,
    val field: String? = null,
    val message: String
)

@Composable
fun ImportReportView(
    summary: ImportSummary,
    onClose: () -> Unit
) {
    val colors = AppTheme.colors
    val hasErrors = summary.failureCount > 0
    val hasSkipped = summary.skippedCount > 0

    Div({
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            padding(Spacing.xxl)
            border(1.px, LineStyle.Solid, Color(colors.border))
            maxWidth(700.px)
            themeTransition()
        }
    }) {
        // Header
        H2({
            style {
                margin(0.px)
                marginBottom(Spacing.lg)
                fontSize(Typography.xl)
                fontWeight("600")
                color(Color(colors.text))
            }
        }) {
            Text("Import Report")
        }

        // Summary cards
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(150px, 1fr))")
                gap(Spacing.md)
                marginBottom(Spacing.xl)
            }
        }) {
            // Total rows
            SummaryCard(
                label = "Total Rows",
                value = summary.totalRows.toString(),
                color = colors.textMuted
            )

            // Success count
            SummaryCard(
                label = "Successful",
                value = summary.successCount.toString(),
                color = colors.success
            )

            // Failure count
            if (hasErrors) {
                SummaryCard(
                    label = "Failed",
                    value = summary.failureCount.toString(),
                    color = colors.danger
                )
            }

            // Skipped count
            if (hasSkipped) {
                SummaryCard(
                    label = "Skipped",
                    value = summary.skippedCount.toString(),
                    color = colors.warning
                )
            }

            // Duration
            summary.durationMs?.let { duration ->
                SummaryCard(
                    label = "Duration",
                    value = formatDuration(duration),
                    color = colors.textMuted
                )
            }
        }

        // Success message
        if (summary.successCount > 0 && !hasErrors) {
            Div({
                style {
                    backgroundColor(Color(if (colors == LightColors) "#ecfdf5" else "#052e16"))
                    border(1.px, LineStyle.Solid, Color(colors.success))
                    borderRadius(BorderRadius.md)
                    padding(Spacing.lg)
                    marginBottom(Spacing.xl)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(Spacing.sm)
                    }
                }) {
                    Span({
                        style {
                            fontSize(Typography.xl)
                        }
                    }) {
                        Text("✓")
                    }
                    Div({
                        style {
                            fontSize(Typography.lg)
                            fontWeight("600")
                            color(Color(if (colors == LightColors) "#065f46" else "#86efac"))
                        }
                    }) {
                        Text("Import completed successfully!")
                    }
                }
                Div({
                    style {
                        fontSize(Typography.base)
                        color(Color(if (colors == LightColors) "#047857" else "#86efac"))
                        marginTop(Spacing.sm)
                    }
                }) {
                    Text("All ${summary.successCount} rows were imported without errors.")
                }
            }
        }

        // Partial success message
        if (summary.successCount > 0 && hasErrors) {
            Div({
                style {
                    backgroundColor(Color(if (colors == LightColors) "#fef3c7" else "#451a03"))
                    border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fbbf24" else "#854d0e"))
                    borderRadius(BorderRadius.md)
                    padding(Spacing.lg)
                    marginBottom(Spacing.xl)
                }
            }) {
                Div({
                    style {
                        fontSize(Typography.lg)
                        fontWeight("600")
                        color(Color(if (colors == LightColors) "#92400e" else "#fcd34d"))
                        marginBottom(Spacing.xs)
                    }
                }) {
                    Text("Import completed with errors")
                }
                Div({
                    style {
                        fontSize(Typography.base)
                        color(Color(if (colors == LightColors) "#78350f" else "#fcd34d"))
                    }
                }) {
                    Text("${summary.successCount} rows imported successfully, but ${summary.failureCount} rows failed.")
                }
            }
        }

        // Complete failure message
        if (summary.successCount == 0 && hasErrors) {
            Div({
                style {
                    backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                    border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fca5a5" else "#7f1d1d"))
                    borderRadius(BorderRadius.md)
                    padding(Spacing.lg)
                    marginBottom(Spacing.xl)
                }
            }) {
                Div({
                    style {
                        fontSize(Typography.lg)
                        fontWeight("600")
                        color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                        marginBottom(Spacing.xs)
                    }
                }) {
                    Text("❌ Import failed")
                }
                Div({
                    style {
                        fontSize(Typography.base)
                        color(Color(colors.danger))
                    }
                }) {
                    Text("All ${summary.failureCount} rows failed to import. Please check the errors below.")
                }
            }
        }

        // Error details
        if (summary.errors.isNotEmpty()) {
            Div({
                style {
                    marginTop(Spacing.xl)
                }
            }) {
                H3({
                    style {
                        margin(0.px)
                        marginBottom(Spacing.md)
                        fontSize(Typography.lg)
                        fontWeight("600")
                        color(Color(colors.text))
                    }
                }) {
                    Text("Error Details (${summary.errors.size})")
                }

                // Error list
                Div({
                    style {
                        maxHeight(300.px)
                        overflow("auto")
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                    }
                }) {
                    summary.errors.forEachIndexed { index, error ->
                        Div({
                            style {
                                padding(Spacing.md)
                                if (index > 0) {
                                    property("border-top", "1px solid ${colors.border}")
                                }
                                property("transition", "background-color 0.2s")
                            }
                            onMouseOver { it.target.asDynamic().style.backgroundColor = colors.surfaceHover }
                            onMouseOut { it.target.asDynamic().style.backgroundColor = "transparent" }
                        }) {
                            // Row number
                            Div({
                                style {
                                    fontSize(13.px)
                                    fontWeight("600")
                                    color(Color(colors.textMuted))
                                    marginBottom(Spacing.xs)
                                }
                            }) {
                                Text("Row ${error.row}")
                                error.field?.let { field ->
                                    Span({
                                        style {
                                            marginLeft(Spacing.sm)
                                            color(Color(colors.textMuted))
                                            fontWeight("normal")
                                        }
                                    }) {
                                        Text("• Field: $field")
                                    }
                                }
                            }

                            // Error message
                            Div({
                                style {
                                    fontSize(Typography.base)
                                    color(Color(colors.danger))
                                }
                            }) {
                                Text(error.message)
                            }
                        }
                    }
                }
            }
        }

        // Close button
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.FlexEnd)
                marginTop(Spacing.xl)
            }
        }) {
            Button({
                onClick { onClose() }
                style {
                    padding(10.px, Spacing.xl)
                    fontSize(Typography.base)
                    fontWeight("500")
                    color(Color("#ffffff"))
                    backgroundColor(Color(colors.primary))
                    border(0.px)
                    borderRadius(BorderRadius.md)
                    cursor("pointer")
                    property("transition", "all 0.2s")
                }
            }) {
                Text("Close")
            }
        }
    }
}

@Composable
private fun SummaryCard(
    label: String,
    value: String,
    color: String
) {
    val colors = AppTheme.colors

    Div({
        style {
            backgroundColor(Color(colors.surfaceHover))
            border(1.px, LineStyle.Solid, Color(colors.border))
            borderRadius(BorderRadius.md)
            padding(Spacing.md)
        }
    }) {
        Div({
            style {
                fontSize(Typography.sm)
                fontWeight("500")
                color(Color(colors.textMuted))
                marginBottom(Spacing.xs)
                property("text-transform", "uppercase")
                property("letter-spacing", "0.05em")
            }
        }) {
            Text(label)
        }
        Div({
            style {
                fontSize(Typography.xxl)
                fontWeight("700")
                color(Color(color))
            }
        }) {
            Text(value)
        }
    }
}

private fun formatDuration(ms: Long): String {
    return when {
        ms < 1000 -> "${ms}ms"
        ms < 60000 -> {
            val seconds = ms / 1000.0
            "${(seconds * 10).toInt() / 10.0}s"
        }
        else -> {
            val minutes = ms / 60000
            val seconds = (ms % 60000) / 1000
            "${minutes}m ${seconds}s"
        }
    }
}

/**
 * Convenience overload that accepts ImportReportDto directly.
 * Used by EpisodeImportForm and OnixImportForm.
 */
@Composable
fun ImportReportView(report: com.castly.shared.dto.ImportReportDto) {
    val colors = AppTheme.colors
    val (statusBg, statusColor, statusText) = when (report.status) {
        "SUCCESS" -> Triple("#dcfce7", "#166534", "Import Successful")
        "PARTIAL" -> Triple("#fef3c7", "#92400e", "Partially Successful")
        else -> Triple("#fee2e2", "#991b1b", "Import Failed")
    }
    Div({
        style {
            backgroundColor(Color(statusBg))
            borderRadius(BorderRadius.md)
            padding(Spacing.md)
            marginBottom(Spacing.md)
        }
    }) {
        Div({ style { fontWeight("600"); color(Color(statusColor)) } }) { Text(statusText) }
        Div({ style { fontSize(Typography.sm); color(Color(statusColor)); marginTop(Spacing.xs) } }) {
            Text("${report.successfulRows} succeeded, ${report.failedRows} failed of ${report.totalRows} total")
        }
    }
    if (report.errors.isNotEmpty()) {
        Div({
            style {
                maxHeight(200.px); overflowY("auto"); padding(Spacing.md)
                backgroundColor(Color(if (colors == com.castly.admin.ui.theme.LightColors) "#fef2f2" else "#450a0a"))
                borderRadius(BorderRadius.md)
            }
        }) {
            report.errors.forEach { err ->
                Div({ style { fontSize(Typography.sm); color(Color(colors.danger)); marginBottom(2.px) } }) {
                    Text("Row ${err.row ?: "-"}: ${err.error}")
                }
            }
        }
    }
}
